import React, { useState, useEffect, useCallback } from 'react';
import { Exam } from '../../types/exam';
import { examApi } from '../../services/exam.service';
import ExamForm from './ExamForm';
import ExamList from './ExamList';
import ExamDetail from './ExamDetail';

type ViewType = 'list' | 'create' | 'detail';

interface ExamManagerProps {
  onClose?: () => void;
}

export const ExamManager: React.FC<ExamManagerProps> = ({ onClose }) => {
  const [view, setView] = useState<ViewType>('list');
  const [exams, setExams] = useState<Exam[]>([]);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isTeacherEligibilityExam = (exam: Exam) => {
    const normalizedTitle = exam.title
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return (
      normalizedTitle === 'teacher eligibility test' ||
      normalizedTitle === 'teachers eligibility test' ||
      normalizedTitle === 'teacher certification exam' ||
      normalizedTitle === 'teachers certification exam'
    );
  };

  const loadExams = useCallback(async () => {
    try {
      setLoading(true);
      const data = await examApi.getTeacherExams();
      const filteredData = data.filter((exam) => !isTeacherEligibilityExam(exam));
      setExams(filteredData);
      setSelectedExam((prev) => {
        if (!prev) return prev;
        const refreshedSelectedExam = filteredData.find((exam) => exam.examId === prev.examId);
        if (!refreshedSelectedExam) {
          setView('list');
          return null;
        }
        return refreshedSelectedExam;
      });
      setError(null);
    } catch (err) {
      setError('Failed to load exams');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const handleCreateExam = async (formData: any) => {
    try {
      setLoading(true);
      const newExam = await examApi.createExam(formData);
      setExams([...exams, newExam]);
      setView('list');
      setError(null);
    } catch (err) {
      setError('Failed to create exam');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExam = async (examId: string, formData: any) => {
    try {
      setLoading(true);
      const updatedExam = await examApi.updateExam(examId, formData);
      setExams(exams.map((e) => (e.examId === examId ? updatedExam : e)));
      setSelectedExam(updatedExam);
      setView('detail');
      setError(null);
    } catch (err) {
      setError('Failed to update exam');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (exam: Exam) => {
    setSelectedExam(exam);
    setView('detail');
  };

  const handleDeleteExam = async (examId: string) => {
    if (!window.confirm('Are you sure you want to delete this exam?')) return;

    try {
      setLoading(true);
      await examApi.deleteExam(examId);
      setExams(exams.filter((e) => e.examId !== examId));
      setView('list');
      setError(null);
    } catch (err) {
      setError('Failed to delete exam');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (examId: string) => {
    try {
      setLoading(true);
      const updatedExam = await examApi.publishExam(examId);
      setExams(exams.map((e) => (e.examId === examId ? updatedExam : e)));
      setSelectedExam(updatedExam);
      setError(null);
    } catch (err) {
      setError('Failed to submit exam for admin approval');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishResults = async (examId: string) => {
    try {
      setLoading(true);
      const updatedExam = await examApi.publishExamResults(examId);
      setExams(exams.map((e) => (e.examId === examId ? updatedExam : e)));
      setSelectedExam(updatedExam);
      setError(null);
    } catch (err) {
      setError('Failed to publish exam results');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Exam Management</h1>
        {view !== 'list' && (
          <button
            onClick={() => setView('list')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
          >
            Back to List
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!loading && view === 'list' && (
        <ExamList
          exams={exams}
          onCreateNew={() => setView('create')}
          onViewDetail={handleViewDetail}
          onDelete={handleDeleteExam}
        />
      )}

      {!loading && view === 'create' && (
        <ExamForm
          onSubmit={handleCreateExam}
          onCancel={() => setView('list')}
        />
      )}

      {!loading && view === 'detail' && selectedExam && (
        <ExamDetail
          exam={selectedExam}
          onUpdate={handleUpdateExam}
          onPublish={handlePublish}
          onPublishResults={handlePublishResults}
          onDelete={handleDeleteExam}
          onRefresh={() => loadExams()}
        />
      )}
    </div>
  );
};

export default ExamManager;
